import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase.js';

export default function SetPasswordPage() {
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('checking'); // checking | form | done

  useEffect(() => {
    // Capture the hash IMMEDIATELY before supabase-js processes and clears it
    const initialHash = window.location.hash;
    const isInviteOrRecovery =
      initialHash.includes('type=invite') ||
      initialHash.includes('type=recovery') ||
      initialHash.includes('access_token');

    let settled = false;
    let fallback = null;
    let sub = null;

    const settle = async (session) => {
      if (settled) return;
      settled = true;
      clearTimeout(fallback);
      sub?.subscription.unsubscribe();

      if (isInviteOrRecovery) {
        // Arrived via invite/recovery link: always show the form; without a
        // session the link was invalid/expired and submitting would fail.
        if (session) {
          setStatus('form');
        } else {
          setError('Invalid or expired invite link. Please request a new one.');
          setStatus('form');
        }
        return;
      }
      // Navigated here directly.
      if (!session) {
        window.location.href = '/auth/sign-in';
        return;
      }
      const { data: userData } = await supabase.auth.getUser();
      const hasDisplayName = !!userData?.user?.user_metadata?.display_name;
      if (hasDisplayName) window.location.href = '/';
      else setStatus('form');
    };

    // Event-driven instead of sleeping: supabase-js processes the URL hash
    // asynchronously and fires SIGNED_IN / INITIAL_SESSION when done. The
    // timeout is a last-resort fallback (e.g. a dead link produces no
    // event), not the primary mechanism.
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) settle(data.session);
    });
    sub = supabase.auth.onAuthStateChange((event, session) => {
      if (session) settle(session);
      else if (event === 'INITIAL_SESSION' && !isInviteOrRecovery) settle(null);
    }).data;
    fallback = setTimeout(() => settle(null), 5000);

    return () => {
      clearTimeout(fallback);
      sub?.subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const trimmedName = displayName.trim();
    if (!trimmedName) { setError('Display name is required'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }

    const { error: err } = await supabase.auth.updateUser({
      password,
      data: { display_name: trimmedName }
    });
    if (err) { setError(err.message); return; }

    // Clear the recovery hash from the URL before redirecting
    window.history.replaceState(null, '', window.location.pathname);

    setStatus('done');
    setTimeout(() => { window.location.href = '/'; }, 2000);
  };

  if (status === 'checking') {
    return (
      <div className="auth-page">
        <div className="auth-page__card">
          <p className="auth-page__message">Verifying...</p>
        </div>
      </div>
    );
  }

  if (status === 'done') {
    return (
      <div className="auth-page">
        <div className="auth-page__card">
          <p className="auth-page__success">Account ready — you're signed in. Redirecting...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page">
      <div className="auth-page__card">
        <h1 className="auth-page__title">Mike's GWS Map</h1>
        <p className="auth-page__sub">Welcome — set your password to continue</p>
        <form onSubmit={handleSubmit} className="auth-page__form">
          <label className="auth-page__field">
            <span>Display Name <span className="auth-page__charcount">({displayName.trim().length}/15)</span></span>
            <input
              type="text"
              value={displayName}
              onChange={e => setDisplayName(e.target.value.slice(0, 15))}
              placeholder="Your name"
              autoFocus
            />
          </label>
          <label className="auth-page__field">
            <span>Password</span>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Min 8 characters"
            />
          </label>
          <label className="auth-page__field">
            <span>Confirm password</span>
            <input
              type="password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder="Re-enter password"
            />
          </label>
          {error && <p className="auth-page__error">{error}</p>}
          <button type="submit" className="btn btn--primary" style={{ width: '100%' }}>
            Set password
          </button>
        </form>
      </div>
    </div>
  );
}